import { randomString } from "@whatslly/commons/utils";
import { CustomError } from "@whatslly/commons/utils";
import { MessagingBackend, InputData, MessageKeys, MessageTypes, OutputData, Request, Response, ErrorResponse } from "./messagingBackend";

export type { Request } from "./messagingBackend";
interface Transaction<T extends MessageTypes> {
  resolve: (value: any) => void;
  reject: (reason: Error) => void;
  controller: AbortController;
  cmd: keyof T;
}

type OptionalSendArgs<T> = T extends undefined ? [undefined?, AbortSignal?] : [T, AbortSignal?];
export class ChunkedMessage<T extends MessageTypes, C extends keyof T> {
  transactionId = randomString(20);
  constructor(
    public readonly cmd: C,
    public readonly data: AsyncIterable<Partial<InputData<T, C>>>
  ) {}

  async *[Symbol.asyncIterator]() {
    let i = 0;
    for await (const data of this.data) {
      yield {
        cmd: this.cmd,
        data,
        transactionId: this.transactionId,
        chunk: i++,
        forwardResponseToWebScript: true,
      };
    }
    yield { cmd: this.cmd, data: {}, transactionId: this.transactionId, chunk: i, chunksDone: true, forwardResponseToWebScript: true };
  }
}

export class MessagingClient<T extends MessageTypes> {
  private readonly transactions = new Map<string, Transaction<T>>();
  respond: <C extends keyof T>(request: Response<T, C> | ErrorResponse<T>) => Promise<void>;
  sendSync: <C extends keyof T>(message: Request<T, C> & { forwardResponseToWebScript?: true }) => void = () => {
    throw new Error("Client not initialized");
  };

  constructor(private readonly messageKeys: MessageKeys<T>) {
    const backend = new MessagingBackend<T>(this.messageKeys);
    backend
      .on(this.messageKeys.RESPONSE, (data: InputData<T, any>, { transactionId }: { transactionId?: string }) => {
        const transaction = transactionId && this.transactions.get(transactionId);
        if (transactionId && transaction) {
          this.transactions.delete(transactionId);
          transaction.resolve(data);
        }
        return undefined as OutputData<T, any>;
      })
      .on(this.messageKeys.ERROR, (data: InputData<T, any>, { transactionId }: { transactionId?: string }) => {
        const transaction = transactionId && this.transactions.get(transactionId);
        if (transactionId && transaction) {
          this.transactions.delete(transactionId);
          transaction.reject(data);
        }
        return undefined as OutputData<T, any>;
      });

    this.respond = backend.getHandler((request: any) => ({ request }));

    if (typeof window !== "undefined" && window.addEventListener) {
      window.addEventListener("message", (event) => {
        if (event?.data?.cmd === this.messageKeys.ERROR && "errorMessage" in event.data) {
          this.respond({ ...event.data, data: new CustomError(event.data.errorMessage, event.data.data) });
        } else {
          this.respond(event.data);
        }
      });
    }
  }

  init(sendSync: typeof MessagingClient.prototype.sendSync) {
    this.sendSync = sendSync;
  }

  private setupTransaction({
    transactionId,
    signal,
    cmd,
    resolve,
    reject,
  }: { transactionId: string; signal?: AbortSignal } & Omit<Transaction<T>, "controller">) {
    const controller = new AbortController();
    this.transactions.set(transactionId, { resolve, reject, controller, cmd });
    controller.signal.addEventListener(
      "abort",
      () => {
        this.transactions.delete(transactionId);
        this.sendSync({ cmd: this.messageKeys.ABORT_TRANSACTION, data: null as never, transactionId });
        reject(controller.signal.reason);
      },
      { once: true }
    );
    signal?.addEventListener("abort", () => controller.abort(), { once: true });
  }

  send<C extends keyof T>(cmd: C, data: InputData<T, C>, signal?: AbortSignal): Promise<OutputData<T, C>>;
  send<C extends keyof T, R extends OutputData<T, C>>(cmd: C, ...args: OptionalSendArgs<InputData<T, C>>): Promise<R>;
  send<C extends keyof T>(cmd: C, ...args: any[]) {
    const [data, signal] = args;
    return new Promise<OutputData<T, C>>((resolve, reject) => {
      const transactionId = randomString(20);
      this.setupTransaction({ transactionId, signal, cmd, resolve, reject });
      this.sendSync({ cmd, data, transactionId, forwardResponseToWebScript: true });
    });
  }

  sendChunked<C extends keyof T, R extends OutputData<T, C>>(...args: OptionalSendArgs<ChunkedMessage<T, C>>) {
    const [msg, signal] = args;
    // eslint-disable-next-line no-async-promise-executor
    return new Promise<R>(async (resolve, reject) => {
      this.setupTransaction({ transactionId: msg.transactionId, signal, cmd: msg.cmd, resolve, reject });
      for await (const chunk of msg) {
        this.sendSync(chunk as any);
      }
    });
  }
}
